import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DeepPartial, deepEqual } from "@tellescope/utils";
import { updateModelWithOptions } from "../utils/update-model-with-options";

export interface UseDebouncedMutationOptions<T extends object> {
  baseValue: T | undefined;
  /**
   * sessionStorage scope AND entity identity. Changing `storageKey` clears
   * in-memory state for the prior entity (its storage entry is left intact).
   * Without `storageKey`, remount via React `key` to swap entities.
   */
  storageKey?: string;
  debounceMs?: number;
  /** Must match the `replaceObjectFields` semantics `commit` sends to the server. */
  replaceObjectFields?: boolean;
  commit: (changes: DeepPartial<T>) => Promise<unknown>;
}

export interface UseDebouncedMutationResult<T extends object> {
  value: T | undefined;
  apply: (changes: DeepPartial<T>) => void;
  /** Awaits any in-flight commit, then fires one more if pending remains. Rejects on commit failure. */
  flush: () => Promise<void>;
  reset: () => void;
  isPending: boolean;
  error: unknown;
}

interface StoredEntry<T extends object> {
  changes: DeepPartial<T>;
  timestamp: number;
}

const DEFAULT_DEBOUNCE_MS = 1500;

const readStorage = <T extends object>(key: string): StoredEntry<T> | null => {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  if (!("changes" in parsed) || !("timestamp" in parsed)) return null;
  const { changes, timestamp } = parsed;
  if (typeof timestamp !== "number") return null;
  if (!changes || typeof changes !== "object") return null;
  // Storage round-trip; inner shape can't be validated against generic T at runtime.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return { changes: changes as DeepPartial<T>, timestamp };
};

const writeStorageEntry = <T extends object>(key: string, entry: StoredEntry<T>): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // ignore quota / serialization errors
  }
};

const removeStorageEntry = (key: string): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

const mergePendingChanges = <T extends object>(
  prev: DeepPartial<T>,
  next: DeepPartial<T>,
  replaceObjectFields: boolean
): DeepPartial<T> => {
  if (replaceObjectFields) {
    return { ...prev, ...next };
  }
  // Match the server's non-replace merge (recursive object merge, array union).
  // DeepPartial<DeepPartial<T>> ≡ DeepPartial<T> structurally, but TS can't see it.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return updateModelWithOptions(prev, next as DeepPartial<typeof prev>, {
    replaceObjectFields: false,
  });
};

export const useDebouncedMutation = <T extends object>({
  baseValue,
  storageKey,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  replaceObjectFields = false,
  commit,
}: UseDebouncedMutationOptions<T>): UseDebouncedMutationResult<T> => {
  const [pendingChanges, setPendingChangesState] = useState<DeepPartial<T> | null>(null);
  const [error, setErrorState] = useState<unknown>(null);

  const pendingChangesRef = useRef<DeepPartial<T> | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const windowStartRef = useRef<number | null>(null);
  // currentKeyRef: which storageKey the in-memory state belongs to (drives entity-switch reset).
  // rehydratedKeyRef: which keys we've already consumed a stored entry for (once per key).
  const currentKeyRef = useRef<string | null>(null);
  const rehydratedKeyRef = useRef<string | null>(null);
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);

  const baseValueRef = useRef(baseValue);
  baseValueRef.current = baseValue;
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const replaceObjectFieldsRef = useRef(replaceObjectFields);
  replaceObjectFieldsRef.current = replaceObjectFields;
  const debounceMsRef = useRef(debounceMs);
  debounceMsRef.current = debounceMs;
  const storageKeyRef = useRef(storageKey);
  storageKeyRef.current = storageKey;

  const setPending = useCallback((next: DeepPartial<T> | null) => {
    pendingChangesRef.current = next;
    setPendingChangesState(next);
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const persist = useCallback(() => {
    const key = storageKeyRef.current;
    if (!key) return;
    const changes = pendingChangesRef.current;
    const start = windowStartRef.current;
    if (!changes || start === null) {
      removeStorageEntry(key);
      return;
    }
    writeStorageEntry<T>(key, { changes, timestamp: start });
  }, []);

  const runCommit = useCallback(async (): Promise<void> => {
    // Don't double-fire while a commit is in flight; flush will await it.
    if (inFlightPromiseRef.current) return;
    clearTimer();
    const changes = pendingChangesRef.current;
    const base = baseValueRef.current;
    const key = storageKeyRef.current;

    if (!changes || !base) {
      windowStartRef.current = null;
      setPending(null);
      if (key) removeStorageEntry(key);
      return;
    }

    const proposed = updateModelWithOptions(base, changes, {
      replaceObjectFields: replaceObjectFieldsRef.current,
    });
    if (deepEqual(base, proposed)) {
      windowStartRef.current = null;
      setPending(null);
      if (key) removeStorageEntry(key);
      setErrorState(null);
      return;
    }

    const snapshot = changes;
    const promise = (async () => {
      try {
        await commitRef.current(snapshot);
        if (pendingChangesRef.current === snapshot) {
          windowStartRef.current = null;
          setPending(null);
          if (key) removeStorageEntry(key);
        } else {
          // Pending changed during commit; reset window so the persisted timestamp
          // ages from the remaining edits, not the cleared snapshot's start.
          windowStartRef.current = Date.now();
          persist();
        }
        setErrorState(null);
      } catch (err) {
        setErrorState(err);
        throw err;
      } finally {
        inFlightPromiseRef.current = null;
      }
    })();
    inFlightPromiseRef.current = promise;
    await promise;
  }, [clearTimer, persist, setPending]);

  const scheduleTimer = useCallback(
    (ms: number) => {
      clearTimer();
      timerRef.current = setTimeout(
        () => {
          // Failures surface via the `error` state.
          runCommit().catch(() => {});
        },
        Math.max(0, ms)
      );
    },
    [clearTimer, runCommit]
  );

  const apply = useCallback(
    (changes: DeepPartial<T>) => {
      const prev = pendingChangesRef.current;
      const merged = prev ? mergePendingChanges<T>(prev, changes, replaceObjectFieldsRef.current) : changes;

      setPending(merged);

      if (windowStartRef.current === null) {
        windowStartRef.current = Date.now();
      }
      persist();
      scheduleTimer(debounceMsRef.current);
    },
    [persist, scheduleTimer, setPending]
  );

  const flush = useCallback(async (): Promise<void> => {
    // Wait for any in-flight commit; swallow its error since the follow-up below
    // will retry. Then commit any remaining pending and propagate that error.
    if (inFlightPromiseRef.current) {
      await inFlightPromiseRef.current.catch(() => {});
    }
    if (pendingChangesRef.current) {
      await runCommit();
    }
  }, [runCommit]);

  const reset = useCallback(() => {
    clearTimer();
    windowStartRef.current = null;
    setPending(null);
    setErrorState(null);
    const key = storageKeyRef.current;
    if (key) removeStorageEntry(key);
  }, [clearTimer, setPending]);

  // Entity-identity reset + rehydration. State adjustments here are seeded from
  // sessionStorage (an external system), not derived from props — the lint rule
  // can't tell the difference.
  /* eslint-disable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change, react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-pass-ref-to-parent */
  useEffect(() => {
    const normalizedKey = storageKey ?? null;

    // storageKey switch = new entity. Drop in-memory state; the prior entity's
    // storage entry stays under its own key for later resume.
    if (currentKeyRef.current !== normalizedKey) {
      clearTimer();
      windowStartRef.current = null;
      pendingChangesRef.current = null;
      setPendingChangesState(null);
      setErrorState(null);
      currentKeyRef.current = normalizedKey;
    }

    // Rehydrate once per key, after baseValue lands.
    if (!storageKey || baseValue === undefined) return;
    if (rehydratedKeyRef.current === storageKey) return;
    rehydratedKeyRef.current = storageKey;

    const entry = readStorage<T>(storageKey);
    if (!entry) return;

    const proposed = updateModelWithOptions(baseValue, entry.changes, { replaceObjectFields });
    if (deepEqual(baseValue, proposed)) {
      removeStorageEntry(storageKey);
      return;
    }

    pendingChangesRef.current = entry.changes;
    setPendingChangesState(entry.changes);
    windowStartRef.current = entry.timestamp;

    const elapsed = Date.now() - entry.timestamp;
    if (elapsed >= debounceMs) {
      runCommit().catch(() => {});
    } else {
      scheduleTimer(debounceMs - elapsed);
    }
  }, [baseValue, clearTimer, debounceMs, replaceObjectFields, runCommit, scheduleTimer, storageKey]);
  /* eslint-enable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change, react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-pass-ref-to-parent */

  // Best-effort flush on tab hide / unload / SPA unmount. pagehide is best-effort
  // under axios-no-keepalive; visibilitychange fires while the page is still alive.
  useEffect(() => {
    const flushIfPending = () => {
      if (pendingChangesRef.current) {
        runCommit().catch(() => {});
      }
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        flushIfPending();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", flushIfPending);
    }
    return () => {
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", flushIfPending);
      }
      flushIfPending();
      clearTimer();
    };
  }, [clearTimer, runCommit]);

  const value = useMemo<T | undefined>(() => {
    if (baseValue === undefined) return undefined;
    if (!pendingChanges) return baseValue;
    return updateModelWithOptions(baseValue, pendingChanges, { replaceObjectFields });
  }, [baseValue, pendingChanges, replaceObjectFields]);

  return {
    value,
    apply,
    flush,
    reset,
    isPending: pendingChanges !== null,
    error,
  };
};
